<script setup lang="ts">
  import { computed, nextTick, onMounted, ref, watch } from 'vue'
  import { FileQuestionMark, FolderPlus } from 'lucide-vue-next'
  import { useUser } from '@/composables/useUser'
  import { useDateRangeStore } from '@/store/dateRangeStore'
  import { getLocalizedDateTimeString } from '@/utils'
  import { Execution } from '@/shared/Executions/models/Execution'
  import { ColumnDefinition } from '@/shared/Table/models/ColumnDefinition'
  import ExecutionNameColumn from './components/ExecutionNameColumn.vue'
  import ExecutionStatusColumn from './components/ExecutionStatusColumn.vue'
  import ExecutionCompletedColumn from './components/ExecutionCompletedColumn.vue'
  import ExecutionEngineVersionmColumn from './components/ExecutionEngineVersionColumn.vue'
  import ExecutionActionsColumn from './components/ExecutionActionsColumn.vue'
  import Table from '@/shared/Table/Table.vue'
  import dayjs from 'dayjs'
  import ExecutionTypeColumn from './components/ExecutionTypeColumn.vue'
  import ExecutionInputDatasetColumn from './components/ExecutionInputDatasetColumn.vue'
  import { HierarchicExecutionViewModel } from './models/HierarchicExecutionViewModel'
  import { HierarchicExecutionService } from './services/HierarchicExecutionService'
  import { InjectionService } from '@/shared/services/InjectionService'
  import { GetExecutionOutputPackageArgs } from '@/shared/Executions/models/GetExecutionOutputPackageArgs'
  import { downloadFile, extractFilename } from '@/utils/download'
  import { AplosFileApiService } from '../FileManager/services/AplosFileApiService'
  import { toast } from 'vue-sonner'
  import {
    ExecutionPhaseType,
    AdvancedAnalysisPhaseType
  } from '@/shared/Executions/models/ExecutionPhaseType'
  import { ArchiveExecutionArgs } from '@/shared/Executions/models/ArchiveExecutionArgs'
  import { CancelExecutionArgs } from '@/shared/Executions/models/CancelExecutionArgs'
  import { UserRoles } from '@/shared/models/UserRoles'
  import { ExecutionContextModel } from '../AnalysisWizard/models/ExecutionContextModel'
  import {
    ExecutionApiService,
    IExecutionApiService
  } from '@/shared/Executions/services/ExecutionApiService'
  import { IExecutionPhaseService } from '@/shared/Executions/services/ExecutionPhaseService'
  import {
    useExecutionHistoryStore,
    createExecutionHistoryStore
  } from '@/store/executionHistoryStore'
  import ExecutionFiltersBar from './components/ExecutionFiltersBar.vue'
  import DeveloperMetricsPanel from './components/DeveloperMetricsPanel.vue'
  import { ExecutionStatusType } from '@/shared/Executions/models/ExecutionStatusType'
  import { ExecutionHistoryReloadActionType } from './models/ExecutionActionReloadActionType'
  import FolderNavigationPanel from './components/FolderNavigationPanel.vue'
  import FolderCreateDialog from './components/FolderCreateDialog.vue'
  import FolderPickerDialog from './components/FolderPickerDialog.vue'
  import { FolderApiService, type IFolderApiService } from './services/FolderApiService'
  import type { Folder } from './models/Folder'

  interface IExecutionHistoryProps {
    displayLimit?: number
    executionSelectionActionEnabled?: boolean
    enablePagination?: boolean
    showExpandToggle?: boolean
    pageSize?: number
    viewMode?: 'tree' | 'flat'
    customActionColumn?: ColumnDefinition<HierarchicExecutionViewModel>
    executionPhaseTypeFilter?: Array<ExecutionPhaseType | typeof AdvancedAnalysisPhaseType> //Only show specific types
    storeInstance?: ReturnType<typeof createExecutionHistoryStore>
    hideReloadActions?: boolean
    showDownloadReport?: boolean
  }

  const props = defineProps<IExecutionHistoryProps>()
  const executionApiService: IExecutionApiService = InjectionService.injectStrict(
    ExecutionApiService.injectableKey
  )

  const aplosFileApiService: AplosFileApiService = InjectionService.injectStrict(
    AplosFileApiService.injectableKey
  )

  const executionPhaseService: IExecutionPhaseService =
    InjectionService.injectStrict('IExecutionPhaseService')

  const folderApiService: IFolderApiService = InjectionService.injectStrict(
    FolderApiService.injectableKey
  )

  const isLoading = ref<boolean>(false)
  const downloadingExecutionId = ref<string>('')
  const downloadingReportExecutionId = ref<string>('')

  // Folder state
  const folders = ref<Folder[]>([])
  const selectedFolderId = ref<string | null>(null)
  const showCreateFolderDialog = ref(false)
  const showFolderPickerDialog = ref(false)
  const moveTargetExecutionId = ref<string | null>(null)
  const moveTargetCurrentFolderId = ref<string | null>(null)

  // Refs and reactive state
  const activeRecord = ref<HierarchicExecutionViewModel | null>(null)
  const searchValue = ref('')

  // When false, date range filtering is applied locally against createdUtc (no API re-fetch).
  // When true, the existing API-based behavior is used (date params sent to the server).
  const useLiveDateFilter = ref(false)

  const viewMode = ref<'tree' | 'flat'>(props.viewMode ?? 'tree')

  // Animation state for showing cancel/archive of executions
  const archivingExecutionIds = ref<Set<string>>(new Set())
  const cancelingExecutionIds = ref<Set<string>>(new Set())
  const removingExecutionIds = ref<Set<string>>(new Set())

  // Get stores and composable methods
  const auth = useUser()

  const isPlatformAdminOrDeveloper = computed(
    () =>
      auth.user.roles?.includes(UserRoles.PlatformAdmin) ||
      auth.user.roles?.includes(UserRoles.PlatformDeveloper)
  )

  const executionHistoryStore = props.storeInstance ?? useExecutionHistoryStore()
  const { executionHistory, loadExecutionHistory, removeByHierarchy } = executionHistoryStore

  const emit = defineEmits<{
    'execution-selected': [execution: HierarchicExecutionViewModel]
    'reload-clicked': [
      execution: HierarchicExecutionViewModel,
      actionType: ExecutionHistoryReloadActionType
    ]
    'reload-advanced-analysis-clicked': [execution: HierarchicExecutionViewModel]
    'download-clicked': [execution: HierarchicExecutionViewModel]
    'download-report-clicked': [execution: HierarchicExecutionViewModel]
    'custom-calculations-clicked': [
      executionContext: ExecutionContextModel,
      actionType: ExecutionHistoryReloadActionType
    ]
    'custom-report-clicked': [
      executionContext: ExecutionContextModel,
      actionType: ExecutionHistoryReloadActionType
    ]
    'post-processing-clicked': [
      executionContext: ExecutionContextModel,
      actionType: ExecutionHistoryReloadActionType
    ]
    'cancel-clicked': [execution: HierarchicExecutionViewModel]
    'archive-clicked': [execution: HierarchicExecutionViewModel]
    'view-progress': [executionId: string, executionName: string]
  }>()

  const onReloadActionClicked = (
    execution: HierarchicExecutionViewModel,
    actionType: ExecutionHistoryReloadActionType
  ): void => {
    emit('reload-clicked', execution, actionType)
  }

  const onReloadAdvancedAnalysisActionClicked = (execution: HierarchicExecutionViewModel): void => {
    emit('reload-advanced-analysis-clicked', execution)
  }

  const onDownloadActionClicked = async (execution: HierarchicExecutionViewModel) => {
    emit('download-clicked', execution)
    downloadingExecutionId.value = execution.id ?? ''
    try {
      await auth.initialize()
      const args = new GetExecutionOutputPackageArgs({
        userId: auth.user.id as string,
        tenantId: auth.user.tenant_id as string,
        executionId: execution.id as string
      })
      const response = await executionApiService.getExecutionOutputPackage(args)
      if (!response.downloadUrl)
        throw Error('Invalid downloadUrl encountered onDownloadActionClicked')
      await downloadFile(response.downloadUrl, extractFilename(response.downloadUrl))
    } catch (error) {
      console.log(error)
      toast.error(`An error occurred trying to download the output package.`)
    } finally {
      downloadingExecutionId.value = ''
    }
  }

  const onDownloadReportActionClicked = async (execution: HierarchicExecutionViewModel) => {
    emit('download-report-clicked', execution)
    downloadingReportExecutionId.value = execution.id ?? ''
    try {
      await auth.initialize()
      const args = new GetExecutionOutputPackageArgs({
        userId: auth.user.id as string,
        tenantId: auth.user.tenant_id as string,
        executionId: execution.id as string
      })
      const response = await executionApiService.getExecutionOutputReport(args)
      if (!response.downloadUrl)
        throw Error('Invalid downloadUrl encountered onDownloadReportActionClicked')
      await downloadFile(response.downloadUrl, extractFilename(response.downloadUrl))
    } catch (error) {
      console.log(error)
      toast.error(`An error occurred trying to download the report.`)
    } finally {
      downloadingReportExecutionId.value = ''
    }
  }

  const buildExecutionContextForExecution = (
    execution: HierarchicExecutionViewModel
  ): ExecutionContextModel => {
    const allExecutions = originalExecutions.value
    const executionMap = new Map<string, Execution>(
      allExecutions.filter((x) => x.id != null).map((x) => [x.id!, x])
    )

    // rootId is a reliable reference to the tree root; fall back to the execution's own id
    // for legacy records that may not have rootId populated.
    const rootExecutionId = execution.rootId ?? execution.id

    // Collect all executions that belong to this hierarchy by walking the tree downward
    function collectDescendants(nodeId: string, map: Map<string, Execution>): Execution[] {
      const result: Execution[] = []
      const node = map.get(nodeId)
      if (!node) return result
      result.push(node)
      for (const exec of allExecutions) {
        if (exec.parentId === nodeId && exec.id) {
          result.push(...collectDescendants(exec.id, map))
        }
      }
      return result
    }

    const executionsForRoot = rootExecutionId
      ? collectDescendants(rootExecutionId, executionMap)
      : [execution]

    const hierarchicExecutions = HierarchicExecutionService.buildHierarchicArray(
      executionsForRoot,
      executionPhaseService
    )

    if (hierarchicExecutions?.length <= 0)
      throw Error('Error building execution context for selected execution!')

    const contextModel = HierarchicExecutionService.buildExecutionContextModelForExecution(
      execution,
      hierarchicExecutions[0],
      executionPhaseService
    )

    return contextModel
  }

  const onCustomCalculationActionClicked = (
    execution: HierarchicExecutionViewModel,
    actionType: ExecutionHistoryReloadActionType
  ): void => {
    const contextModel = buildExecutionContextForExecution(execution)
    emit('custom-calculations-clicked', contextModel, actionType)
  }

  const onPostProcessingActionClicked = (
    execution: HierarchicExecutionViewModel,
    actionType: ExecutionHistoryReloadActionType
  ): void => {
    const contextModel = buildExecutionContextForExecution(execution)
    emit('post-processing-clicked', contextModel, actionType)
  }

  const onCustomReportActionClicked = (
    execution: HierarchicExecutionViewModel,
    actionType: ExecutionHistoryReloadActionType
  ): void => {
    const contextModel = buildExecutionContextForExecution(execution)
    emit('custom-report-clicked', contextModel, actionType)
  }

  const onCancelActionClicked = (execution: HierarchicExecutionViewModel): void => {
    emit('cancel-clicked', execution)
    if (execution?.id) cancelExecution(execution.id)
  }

  const onArchiveActionClicked = (execution: HierarchicExecutionViewModel): void => {
    emit('archive-clicked', execution)
    if (execution?.id) archiveExecution(execution?.id)
  }

  // --- Folder operations ---

  async function loadFolders() {
    try {
      await auth.initialize()
      const response = await folderApiService.listFolders(
        auth.user.tenant_id as string,
        auth.user.id as string
      )
      folders.value = response.data.folders ?? []
    } catch (error) {
      console.error('Error loading folders:', error)
    }
  }

  async function handleFolderSelected(folderId: string | null) {
    selectedFolderId.value = folderId
    // Update the store's folderId and reload executions
    executionHistory.folderId = folderId ?? undefined
    isLoading.value = true
    await loadExecutionHistory(true)
    isLoading.value = false
  }

  function handleCreateFolderClick() {
    showCreateFolderDialog.value = true
  }

  async function handleFolderCreated(_folder: Folder) {
    await loadFolders()
  }

  function handleMoveToFolderClick(execution: HierarchicExecutionViewModel) {
    moveTargetExecutionId.value = execution.id ?? null
    // We don't have folder_id on the execution model from the API yet,
    // so pass null as current folder (the picker will show all options)
    moveTargetCurrentFolderId.value = null
    showFolderPickerDialog.value = true
  }

  async function handleFolderChosen(folderId: string | null) {
    if (!moveTargetExecutionId.value) return

    try {
      await auth.initialize()
      await folderApiService.moveToFolder(
        auth.user.tenant_id as string,
        auth.user.id as string,
        moveTargetExecutionId.value,
        folderId
      )
      toast.success('Execution moved successfully.')
      // If we're viewing a specific folder, refresh the list
      if (selectedFolderId.value !== null) {
        isLoading.value = true
        await loadExecutionHistory(true)
        isLoading.value = false
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to move execution.'
      toast.error(message)
    } finally {
      moveTargetExecutionId.value = null
      moveTargetCurrentFolderId.value = null
    }
  }

  const onToggleViewMode = () => {
    viewMode.value = viewMode.value !== 'tree' ? 'tree' : 'flat'
  }

  const createColumnDefinitions = (
    customActionColumn?: ColumnDefinition<HierarchicExecutionViewModel>
  ): Array<ColumnDefinition<HierarchicExecutionViewModel>> => {
    const columns = new Array<ColumnDefinition<HierarchicExecutionViewModel>>()

    const nameColumn = new ColumnDefinition<HierarchicExecutionViewModel>('name', 'name', 'Name')
    nameColumn.cellComponent = ExecutionNameColumn
    nameColumn.width = 200
    columns.push(nameColumn)

    const typeColumn = new ColumnDefinition<HierarchicExecutionViewModel>(undefined, 'type', 'Type')
    typeColumn.cellComponent = ExecutionTypeColumn
    typeColumn.width = 120
    typeColumn.cellProps = {
      excecutionPhaseService: executionPhaseService
    }
    typeColumn.clickable = true
    columns.push(typeColumn)

    const inputDatasetColumn = new ColumnDefinition<HierarchicExecutionViewModel>(
      'input_dataset',
      'input_dataset',
      'Input Dataset'
    )
    inputDatasetColumn.cellComponent = ExecutionInputDatasetColumn
    inputDatasetColumn.cellProps = {
      aplosFileApiService: aplosFileApiService
    }
    columns.push(inputDatasetColumn)

    const statusColumn = new ColumnDefinition<HierarchicExecutionViewModel>(
      'status',
      'status',
      'Status'
    )
    statusColumn.cellComponent = ExecutionStatusColumn
    statusColumn.clickable = true
    columns.push(statusColumn)

    const completedColumn = new ColumnDefinition<HierarchicExecutionViewModel>(
      'completedUtc',
      'completedUtc',
      'Timestamp'
    )
    completedColumn.cellComponent = ExecutionCompletedColumn
    completedColumn.clickable = true
    columns.push(completedColumn)

    const engineVersionColumn = new ColumnDefinition<HierarchicExecutionViewModel>(
      'execution_engine_version',
      'execution_engine_version',
      'Engine Version'
    )
    engineVersionColumn.cellComponent = ExecutionEngineVersionmColumn
    engineVersionColumn.cellProps = {
      executionPhaseService: executionPhaseService
    }
    engineVersionColumn.clickable = true
    columns.push(engineVersionColumn)

    const defaultActionsColumn = new ColumnDefinition<HierarchicExecutionViewModel>(
      'actions',
      'actions',
      'Actions'
    )
    defaultActionsColumn.cellComponent = ExecutionActionsColumn
    defaultActionsColumn.cellProps = {
      onReloadClicked: (
        record: HierarchicExecutionViewModel,
        actionType: ExecutionHistoryReloadActionType
      ) => onReloadActionClicked(record, actionType),
      onReloadAdvancedAnalysisClicked: (record: HierarchicExecutionViewModel) =>
        onReloadAdvancedAnalysisActionClicked(record),
      onDownloadClicked: (record: HierarchicExecutionViewModel) => onDownloadActionClicked(record),
      onCustomCalculationsClicked: (
        record: HierarchicExecutionViewModel,
        actionType: ExecutionHistoryReloadActionType
      ) => onCustomCalculationActionClicked(record, actionType),
      onPostProcessingClicked: (
        record: HierarchicExecutionViewModel,
        actionType: ExecutionHistoryReloadActionType
      ) => onPostProcessingActionClicked(record, actionType),
      onCustomReportClicked: (
        record: HierarchicExecutionViewModel,
        actionType: ExecutionHistoryReloadActionType
      ) => onCustomReportActionClicked(record, actionType),
      onCancelClicked: (record: HierarchicExecutionViewModel) => onCancelActionClicked(record),
      onArchiveClicked: (record: HierarchicExecutionViewModel) => onArchiveActionClicked(record),
      onMoveToFolderClicked: (record: HierarchicExecutionViewModel) => handleMoveToFolderClick(record),
      archivingExecutionIds: archivingExecutionIds.value,
      cancelingExecutionIds: cancelingExecutionIds.value,
      downloadingExecutionId: downloadingExecutionId.value,
      downloadingReportExecutionId: downloadingReportExecutionId.value,
      executionPhaseService: executionPhaseService,
      hasAdminAccess: isPlatformAdminOrDeveloper,
      hideReloadActions: props.hideReloadActions,
      showDownloadReport: props.showDownloadReport,
      onDownloadReportClicked: (record: HierarchicExecutionViewModel) =>
        onDownloadReportActionClicked(record)
    }
    columns.push(customActionColumn ?? defaultActionsColumn)

    return columns
  }

  const tableColumns = computed(() => {
    return createColumnDefinitions(props.customActionColumn)
  })

  const currentPage = ref(1)

  // Date range store
  const dateRangeStore = useDateRangeStore()

  watch(
    () =>
      [
        searchValue.value,
        executionHistory.executionStatusType,
        executionHistory.executionType,
        dateRangeStore.startDate,
        dateRangeStore.endDate
      ] as const,
    () => {
      currentPage.value = 1
    }
  )

  const paginationConfig = computed(() => {
    if (!props.enablePagination) return false
    return {
      showSizeChanger: props.pageSize != null && originalExecutions.value?.length >= props.pageSize,
      pageSize: props.pageSize ?? 20,
      current: currentPage.value,
      onChange: (page: number) => {
        currentPage.value = page
      },
      position: ['bottomRight', 'topRight'] as const
    }
  })

  const tableDataSource = computed(() => {
    if (isLoading.value) {
      return Array.from({ length: 10 }).map((_, i) => ({ __skeleton: true, id: `skeleton-${i}` }))
    }
    return displayedExecutions.value ?? []
  })

  // Use computed properties so filtering happens automatically
  const originalExecutions = computed<Execution[]>(() => {
    const all = executionHistory.executions ?? []
    const phaseFilters = props.executionPhaseTypeFilter
    if (!phaseFilters?.length) return all
    return all.filter((execution) => {
      const phase = executionPhaseService.tryGetPrimaryPhaseTypeForExecution(execution)
      return phase && phaseFilters.indexOf(phase) !== -1
    })
  })

  // Flat annotated list: primaryExecutionPhase and operableInputFileId populated for every item.
  // Acts as a typed intermediate between originalExecutions and filteredExecutions so that
  // filters that depend on annotated fields (e.g. executionType) can live in filteredExecutions.
  const annotatedExecutions = computed<HierarchicExecutionViewModel[]>(() =>
    HierarchicExecutionService.buildArray(originalExecutions.value, executionPhaseService)
  )

  // Pre-built hierarchic tree cached independently of filter/search state.
  // Used by displayedExecutions so tree construction is not repeated on every keystroke.
  const hierarchicExecutions = computed<HierarchicExecutionViewModel[]>(() =>
    HierarchicExecutionService.buildHierarchicArray(originalExecutions.value, executionPhaseService)
  )

  const archiveExecution = async (executionId: string) => {
    archivingExecutionIds.value.add(executionId)

    try {
      const archiveExecutionArgs = new ArchiveExecutionArgs({
        userId: auth.user.id!,
        tenantId: auth.user.tenant_id!,
        executionId: executionId!
      })
      const archiveExecutionResponse =
        await executionApiService.archiveExecution(archiveExecutionArgs)
      removingExecutionIds.value.add(executionId)
      if (archiveExecutionResponse.data.error != null) {
        toast.error('An error was encountered trying to archive an execution.')
      } else {
        toast.success('Execution archived successfully.')
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to archive execution.')
    } finally {
      setTimeout(() => {
        // The backend archives the target and its descendants (not parents/siblings).
        // Remove the target subtree from the store to stay in sync.
        removeByHierarchy(executionId)
        archivingExecutionIds.value.delete(executionId)
        removingExecutionIds.value.delete(executionId)
      }, 600) // Animation duration
    }
  }

  const cancelExecution = async (executionId: string) => {
    cancelingExecutionIds.value.add(executionId)

    try {
      const cancelExecutionArgs = new CancelExecutionArgs({
        userId: auth.user.id!,
        tenantId: auth.user.tenant_id!,
        executionId: executionId!
      })
      const cancelExecutionResponse = await executionApiService.cancelExecution(cancelExecutionArgs)
      const cancelledExecution = executionHistory.executions.find((x) => x.id === executionId)

      if (cancelledExecution == null || cancelExecutionResponse.data.error != null) {
        toast.error(
          `An error was encountered trying to cancel an execution: ${cancelExecutionResponse.data.error?.message}`
        )
      } else {
        cancelledExecution.status = cancelExecutionResponse.data.currentStatus
        toast.success('Execution canceled successfully.')
      }
    } catch (error: any) {
      const _error =
        error?.response?.data?.error?.message || error?.message || 'Failed to cancel execution.'
      toast.error(_error)
    } finally {
      cancelingExecutionIds.value.delete(executionId)
    }
  }

  // Format date range for display
  const formattedDateRange = computed(() => {
    const startDate = dayjs(dateRangeStore.startDate)
    const endDate = dayjs(dateRangeStore.endDate)

    return {
      start: startDate.format('MMM D, YYYY'),
      end: endDate.format('MMM D, YYYY'),
      full: `${startDate.format('MMM D, YYYY')} - ${endDate.format('MMM D, YYYY')}`
    }
  })

  // Function to get messages for the menu bar
  function getMessages() {
    const messages: string[] = []

    // Add date range active message if date range is active
    if (dateRangeStore.isActive) {
      console.debug(`Date range: ${dateRangeStore.startDate} to ${dateRangeStore.endDate}`)
    }

    // Add last refresh time message if available
    if (executionHistory.lastFetch) {
      const lastRefreshTime = getLocalizedDateTimeString(executionHistory.lastFetch)
      messages.push(`Last refreshed: ${lastRefreshTime}`)
    }

    // Add next refresh time for debugging purposes
    // Find the getExecutions task in the refresh manager
    try {
      // @ts-ignore - Access private tasks property for debugging
      const refreshManagerTasks = window.refreshManager?.tasks
      if (refreshManagerTasks) {
        const executionsTask = refreshManagerTasks.get('getExecutions')
        if (executionsTask) {
          const nextRefreshTime = new Date(Date.now() + executionsTask.interval)
          const formattedTime = nextRefreshTime.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
          })
          console.debug(
            `[DEBUG] Next refresh: ${formattedTime} (interval: ${executionsTask.interval}ms)`
          )

          // If there's an active interval timeout, show that too
          if (executionsTask.active_interval_timeout) {
            const activeUntil = new Date(executionsTask.active_interval_timeout)
            const activeUntilFormatted = activeUntil.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: true
            })
            console.debug(`[DEBUG] Active refresh until: ${activeUntilFormatted}`)
          }
        }
      }
    } catch (error) {
      console.error('Error getting refresh debug info:', error)
    }

    return messages
  }

  const filteredExecutions = computed<HierarchicExecutionViewModel[]>(() => {
    let results = annotatedExecutions.value

    if (executionHistory.executionStatusType.length > 0) {
      results = results.filter((execution) =>
        executionHistory.executionStatusType.includes(execution.status as ExecutionStatusType)
      )
    }

    if (executionHistory.executionType.length > 0) {
      results = results.filter((execution) =>
        executionHistory.executionType.includes(
          execution.primaryExecutionPhase as ExecutionPhaseType | typeof AdvancedAnalysisPhaseType
        )
      )
    }

    if (!useLiveDateFilter.value && dateRangeStore.isActive
        && dateRangeStore.startDate && dateRangeStore.endDate) {
      const startMs = dayjs(dateRangeStore.startDate).valueOf()
      const endMs   = dayjs(dateRangeStore.endDate).valueOf()
      results = results.filter((execution) => {
        if (!execution.createdUtc) return true
        const ms = dayjs(execution.createdUtc).valueOf()
        return ms >= startMs && ms <= endMs
      })
    }

    if (!searchValue.value) return results

    return results.filter((execution) => {
      const executionId = execution.id ? execution.id.toLowerCase().trim() : ''
      const fileName = execution.name ? execution.name.toLowerCase().trim() : ''
      const endDate = execution.completedUtc
        ? getLocalizedDateTimeString(execution.completedUtc).toLowerCase().trim()
        : ''
      const fieldsToCheck = [fileName, endDate, executionId]
      return fieldsToCheck.some((field) => field.includes(searchValue.value))
    })
  })

  const isFiltered = computed(
    () =>
      !!(
        searchValue.value ||
        executionHistory.executionStatusType.length > 0 ||
        executionHistory.executionType.length > 0 ||
        (!useLiveDateFilter.value && dateRangeStore.isActive)
      )
  )

  const displayedExecutions = computed<HierarchicExecutionViewModel[]>(() => {
    if (!isFiltered.value) {
      if (viewMode.value === 'tree') {
        return hierarchicExecutions.value
      } else {
        return annotatedExecutions.value
      }
    } else {
      if (viewMode.value === 'tree') {
        const matchedIds = new Set<string>(
          filteredExecutions.value.map((e) => e.id).filter(Boolean) as string[]
        )
        const hasNonDateFilter = !!(
          searchValue.value ||
          executionHistory.executionStatusType.length > 0 ||
          executionHistory.executionType.length > 0
        )
        return HierarchicExecutionService.filterHierarchicArray(
          hierarchicExecutions.value,
          matchedIds,
          undefined,
          !hasNonDateFilter
        )
      } else {
        return filteredExecutions.value
      }
    }
  })

  const activeStatuses = new Set<ExecutionStatusType>([
    ExecutionStatusType.Queued,
    ExecutionStatusType.Pending,
    ExecutionStatusType.Throttled,
    ExecutionStatusType.Running,
    ExecutionStatusType.Unexecuted
  ])

  const autoExpandActive = ref<boolean>(true)

  function handleToggleAutoExpandActive() {
    autoExpandActive.value = !autoExpandActive.value
  }

  function hasActiveDescendant(node: Execution): boolean {
    const children = (node as any).children as Execution[] | undefined
    if (!children?.length) return false
    return children.some(
      (c) => (c.status && activeStatuses.has(c.status)) || hasActiveDescendant(c)
    )
  }

  function collectExpandableIds(nodes: Execution[]): string[] {
    const ids: string[] = []
    for (const node of nodes) {
      const children = (node as any).children as Execution[] | undefined
      if (children?.length) {
        if (node.id) ids.push(node.id)
        ids.push(...collectExpandableIds(children))
      }
    }
    return ids
  }

  function collectActiveExpandableIds(nodes: Execution[]): string[] {
    const ids: string[] = []
    for (const node of nodes) {
      const children = (node as any).children as Execution[] | undefined
      if (children?.length) {
        if ((node.status && activeStatuses.has(node.status)) || hasActiveDescendant(node)) {
          if (node.id) ids.push(node.id)
        }
        ids.push(...collectActiveExpandableIds(children))
      }
    }
    return ids
  }

  function hasMatchedDescendant(node: HierarchicExecutionViewModel): boolean {
    if (node.hasMatchedSearch) return true
    const children = node.children as HierarchicExecutionViewModel[] | undefined
    return !!children?.some(hasMatchedDescendant)
  }

  // Only expands nodes that directly match or have a matching descendant.
  // Used when filters are active so that only paths leading to matches are expanded.
  function collectMatchedExpandableIds(nodes: HierarchicExecutionViewModel[]): string[] {
    const ids: string[] = []
    for (const node of nodes) {
      const children = node.children as HierarchicExecutionViewModel[] | undefined
      if (children?.length) {
        if (hasMatchedDescendant(node)) {
          if (node.id) ids.push(node.id)
        }
        ids.push(...collectMatchedExpandableIds(children))
      }
    }
    return ids
  }

  const expandedRowKeys = ref<string[]>([])

  const allExpandableRowKeys = computed(() => collectExpandableIds(displayedExecutions.value))

  // When the view mode changes, reset expand state and disable auto-expand when going flat.
  watch(viewMode, (newMode) => {
    manuallyExpanded.clear()
    manuallyCollapsed.clear()
    expandedRowKeys.value = []
    if (newMode === 'flat' && autoExpandActive.value) {
      autoExpandActive.value = false
    }
  })

  // Rows the user has explicitly expanded/collapsed, overriding auto-expand logic.
  // Cleared whenever the user intentionally changes filters or the auto-expand toggle
  // (structural changes), but preserved across background data refreshes.
  const manuallyExpanded = new Set<string>()
  const manuallyCollapsed = new Set<string>()

  watch(
    () =>
      [
        searchValue.value,
        executionHistory.executionStatusType,
        executionHistory.executionType,
        displayedExecutions.value,
        autoExpandActive.value,
        isFiltered.value
      ] as const,
    ([search, executionStatusType, executionType, executions, autoExpand, filtered], prev) => {
      const prevSearch = prev?.[0]
      const prevStatusType = prev?.[1]
      const prevExecutionType = prev?.[2]
      const prevAutoExpand = prev?.[4]
      const prevFiltered = prev?.[5] ?? false
      // filtersActive includes date range via isFiltered so that date-only filtering
      // also triggers auto-expand of matched tree nodes.
      const filtersActive = filtered
      const prevFiltersActive = prevFiltered
      const filtersJustCleared = prevFiltersActive && !filtersActive

      const structuralChange =
        (filtersActive &&
          (search !== prevSearch ||
            executionStatusType !== prevStatusType ||
            executionType !== prevExecutionType ||
            filtered !== prevFiltered)) ||
        (autoExpand !== prevAutoExpand && autoExpand === true)

      if (structuralChange || filtersJustCleared) {
        manuallyExpanded.clear()
        manuallyCollapsed.clear()
      }

      const keys = new Set<string>()
      if (filtersActive) {
        // Only expand nodes that contain a match so unrelated branches stay collapsed
        for (const id of collectMatchedExpandableIds(executions)) keys.add(id)
      } else if (!filtersJustCleared) {
        // No active filters — preserve existing expanded state
        for (const id of expandedRowKeys.value) keys.add(id)
      }
      // else filtersJustCleared → start fresh; only auto-expand and manual overrides apply
      if (autoExpand) {
        for (const id of collectActiveExpandableIds(executions)) keys.add(id)
      }

      // Apply user overrides on top of auto-computed keys so manual
      // expand/collapse decisions survive background data refreshes.
      for (const id of manuallyExpanded) keys.add(id)
      for (const id of manuallyCollapsed) keys.delete(id)

      expandedRowKeys.value = Array.from(keys)
    },
    { immediate: true }
  )

  // Preserve the current scroll position whenever tableDataSource is replaced or mutated.
  // Both archive (splice) and background refresh (mergeExecutions / full replace) cause
  // ant-design-vue to receive a new dataSource reference, which resets scroll without this.
  // The pending flag ensures only the first flush:sync firing per reactive batch captures
  // the position; subsequent firings within the same batch are skipped, avoiding
  // redundant nextTick scroll restorations.
  let scrollRestorePending = false
  watch(
    tableDataSource,
    () => {
      if (scrollRestorePending) return
      scrollRestorePending = true
      const y = window.scrollY
      nextTick(() => {
        window.scrollTo({ top: y, behavior: 'instant' })
        scrollRestorePending = false
      })
    },
    { flush: 'sync' }
  )

  function handleExpandAll() {
    manuallyCollapsed.clear()
    autoExpandActive.value = false
    expandedRowKeys.value = allExpandableRowKeys.value
  }

  function handleExpandedRowsChange(keys: string[]) {
    const prevKeys = new Set(expandedRowKeys.value)
    const newKeys = new Set(keys)

    // Special case: "collapse all" from the expand toggle.
    // Fully collapse and disable auto-expand so the user's choice is respected.
    if (keys.length === 0) {
      manuallyExpanded.clear()
      manuallyCollapsed.clear()
      autoExpandActive.value = false
      const scrollY = window.scrollY
      expandedRowKeys.value = []
      nextTick(() => {
        window.scrollTo({ top: scrollY, behavior: 'instant' })
      })
      return
    }

    // Special case: "expand all" from the expand toggle.
    // Expand all rows and disable auto-expand so background refreshes don't interfere.
    const allExpandable = allExpandableRowKeys.value
    if (allExpandable.length > 0 && keys.length === allExpandable.length) {
      const incomingSet = new Set(keys)
      if (allExpandable.every((k) => incomingSet.has(k))) {
        manuallyCollapsed.clear()
        autoExpandActive.value = false
        expandedRowKeys.value = keys
        return
      }
    }

    // Record what the user manually changed so the auto-expand watch
    // can preserve these decisions across background data refreshes.
    let userMadeChange = false
    for (const id of newKeys) {
      if (!prevKeys.has(id)) {
        manuallyExpanded.add(id)
        manuallyCollapsed.delete(id)
        userMadeChange = true
      }
    }
    for (const id of prevKeys) {
      if (!newKeys.has(id)) {
        manuallyCollapsed.add(id)
        manuallyExpanded.delete(id)
        userMadeChange = true
      }
    }

    // Any manual expand/collapse means the user is managing state themselves;
    // disable auto-expand so background refreshes don't override their choices.
    if (userMadeChange && autoExpandActive.value) {
      // Seed manuallyExpanded with all rows currently expanded via auto-expand
      // so they survive the transition to manual mode.
      for (const id of prevKeys) {
        if (!manuallyCollapsed.has(id)) {
          manuallyExpanded.add(id)
        }
      }
      autoExpandActive.value = false
    }

    const isCollapsing = keys.length < expandedRowKeys.value.length
    if (isCollapsing) {
      const scrollY = window.scrollY
      expandedRowKeys.value = keys
      nextTick(() => {
        window.scrollTo({ top: scrollY, behavior: 'instant' })
      })
    } else {
      expandedRowKeys.value = keys
    }
  }

  const scrollConfig = computed(() => (tableDataSource.value?.length ? { x: true } : undefined))

  const footerMessage = computed(() => {
    const count = executionHistory.executions.length
    const range = dateRangeStore.isActive ? ` (${formattedDateRange.value.full})` : ''
    if (count === 0) return `No records available${range}`
    if (executionHistory.hasLoadingError)
      return `${count} records loaded${range} — more records exist but couldn't be retrieved at this time`
    if (isFiltered.value) {
      const filteredCount = filteredExecutions.value.length
      return viewMode.value === 'tree'
        ? `Matched ${filteredCount} of all ${count} records loaded${range}`
        : `Showing ${filteredCount} of all ${count} records loaded${range}`
    }
    return `All ${count} records loaded${range}`
  })

  async function handleRefresh() {
    // Reset any pagination state in the store
    isLoading.value = true
    await loadExecutionHistory(false)
    isLoading.value = false
  }

  let searchDebounceTimer: ReturnType<typeof setTimeout> | undefined
  const handleSearch = (value: string) => {
    if (searchDebounceTimer !== undefined) clearTimeout(searchDebounceTimer)
    searchDebounceTimer = setTimeout(() => {
      searchValue.value = value
    }, 200)
  }

  // Handle date range changes
  const handleDateRangeChanged = async () => {
    if (useLiveDateFilter.value) {
      // API-based: forward date range to the store and re-fetch
      executionHistory.startDate = dateRangeStore.isActive ? dateRangeStore.startDate : undefined
      executionHistory.endDate   = dateRangeStore.isActive ? dateRangeStore.endDate   : undefined
      isLoading.value = true
      await loadExecutionHistory(true)
      isLoading.value = false
    }
    // else: filteredExecutions reacts to dateRangeStore changes automatically — no API call needed
  }

  // Updated setActiveAnalysisRecord
  function handleCellClick(record: HierarchicExecutionViewModel) {
    activeRecord.value = record
    emit('execution-selected', record)
    emit('view-progress', record.id ?? '', record.name ?? 'Execution')
  }

  //TODO hook up matching with search once we have hierarchic view model for executions in place
  //@ts-ignore
  function getRowClassName(record: HierarchicExecutionViewModel) {
    const classes = new Array<string>()
    if (removingExecutionIds.value.has(record.id ?? '')) {
      classes.push('row-delete-anim')
    }
    if (record.hasMatchedSearch) {
      classes.push('matched-search')
    }
    return classes.join(' ')
  }

  // Lifecycle hooks
  onMounted(async () => {
    isLoading.value = true
    await auth.initialize()

    // Load folders for the navigation panel (dev-only feature)
    if (isPlatformAdminOrDeveloper.value) {
      loadFolders()
    }

    // Only set default date range if no date range is already set
    // This preserves the date range when navigating away and back
    if (!dateRangeStore.startDate || !dateRangeStore.endDate) {
      dateRangeStore.set60DayRange()
    }
    if (useLiveDateFilter.value) {
      executionHistory.startDate = dateRangeStore.startDate
      executionHistory.endDate   = dateRangeStore.endDate
    } else {
      executionHistory.startDate = undefined
      executionHistory.endDate   = undefined
    }

    // Only load data if we don't already have executions loaded
    // or if the data is stale (older than 5 minutes)
    // or if we still have skeletonized records
    const dataIsStale = Date.now() - executionHistory.lastFetch > 5 * 60 * 1000
    const needsInitialLoad =
      executionHistory.executions.length === 0 ||
      (executionHistory.executions.length === 10 && !executionHistory.executions[0].id)

    if (needsInitialLoad || dataIsStale) {
      await loadExecutionHistory(false)
    }
    isLoading.value = false
  })
</script>

<template>
  <div class="relative flex gap-4">
    <!-- Folder Navigation Sidebar (platform_developer only — feature in dev) -->
    <aside v-if="isPlatformAdminOrDeveloper" class="w-56 flex-shrink-0 border-r border-gray-200 pr-4 dark:border-gray-700">
      <div class="mb-2 flex items-center justify-between px-2">
        <span class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Folders</span>
        <button
          class="rounded p-1 text-muted-foreground hover:bg-gray-100 hover:text-foreground dark:hover:bg-gray-800"
          title="Create Folder"
          @click="handleCreateFolderClick"
          data-testid="create-folder-btn"
        >
          <FolderPlus :size="16" />
        </button>
      </div>
      <FolderNavigationPanel
        :folders="folders"
        :selected-folder-id="selectedFolderId"
        @folder-selected="handleFolderSelected"
      />
    </aside>

    <!-- Main Content -->
    <div class="min-w-0 flex-1">
      <ExecutionFiltersBar
        @search-value-changed="handleSearch"
        @date-ranged-changed="handleDateRangeChanged"
        @refresh-clicked="handleRefresh"
        @toggle-auto-expand-active="handleToggleAutoExpandActive"
        @toggle-view-mode="onToggleViewMode"
        :searchValue="searchValue"
        :messages="getMessages()"
        :isRefreshing="isLoading"
        :autoExpandActive="autoExpandActive"
        :activeStatuses="activeStatuses"
        :viewMode="viewMode"
        :storeInstance="executionHistoryStore"
      ></ExecutionFiltersBar>

      <Table
        :data-source="tableDataSource"
        :column-definitions="tableColumns"
        :expandedRowKeys="expandedRowKeys"
        :all-expandable-row-keys="allExpandableRowKeys"
        :show-expand-toggle="showExpandToggle"
        :expand-toggle-disabled="viewMode === 'flat' || !!(searchValue || executionHistory.executionStatusType.length > 0 || executionHistory.executionType.length > 0)"
        @expand-all="handleExpandAll"
        @expanded-rows-change="handleExpandedRowsChange"
        :emit-row-selection="props.executionSelectionActionEnabled ?? false"
        @row-selected="handleCellClick"
        :pagination-config="paginationConfig"
        :loading="isLoading"
        :rowClassName="getRowClassName"
        :class="['ant-table-striped ant-table-hover overflow-hidden rounded-lg']"
        :scroll="scrollConfig"
      >
        <template #footer>
          <div v-if="executionHistory.isLoadingMore" class="flex flex-col items-center py-4">
            <a-spin tip="Loading more results..." />
            <div class="mt-2 text-sm text-gray-500">
              {{ executionHistory.executions.length }} records loaded so far
              <span v-if="dateRangeStore.isActive" class="ml-1">({{ formattedDateRange.full }})</span>
            </div>
          </div>
          <div v-else class="flex flex-col items-center py-4 text-sm text-gray-500">
            <span :class="{ 'text-red-500': executionHistory.hasLoadingError }">
              {{ footerMessage }}
            </span>
          </div>
        </template>

        <template #emptyText>
          <div class="py-10">
            <slot name="empty-state">
              <FileQuestionMark class="mx-auto mb-5 size-14 text-muted-foreground" />
              <div class="text-muted-foreground">
                <template v-if="selectedFolderId && selectedFolderId !== 'unassigned'">
                  This folder has no executions. Move executions here using the folder action on each row.
                </template>
                <template v-else-if="selectedFolderId === 'unassigned'">
                  All executions are assigned to folders.
                </template>
                <template v-else>
                  No records
                </template>
              </div>
            </slot>
          </div>
        </template>
      </Table>

      <DeveloperMetricsPanel />
    </div>

    <!-- Folder Create Dialog (platform_developer only) -->
    <FolderCreateDialog
      v-if="isPlatformAdminOrDeveloper"
      v-model:open="showCreateFolderDialog"
      :folders="folders"
      :tenant-id="(auth.user.tenant_id as string) ?? ''"
      :user-id="(auth.user.id as string) ?? ''"
      @folder-created="handleFolderCreated"
    />

    <!-- Folder Picker Dialog (platform_developer only) -->
    <FolderPickerDialog
      v-if="isPlatformAdminOrDeveloper"
      v-model:visible="showFolderPickerDialog"
      :folders="folders"
      :current-folder-id="moveTargetCurrentFolderId"
      @folder-chosen="handleFolderChosen"
    />
  </div>
</template>
